import React, { useState } from 'react'
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { AppBar, Toolbar, Typography, Container, Box, Button, CircularProgress, Tabs, Tab } from '@mui/material'
import { AdminPanelSettings as AdminIcon, Dashboard as DashboardIcon, Analytics as AnalyticsIcon, TrendingUp as OTDRIcon } from '@mui/icons-material'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ProjectProvider, useProject } from './contexts/ProjectContext'
import AuthPage from './components/AuthPage'
import ProjectSelector from './components/ProjectSelector'
import AdminPanel from './components/AdminPanel'
import Dashboard from './pages/Dashboard'
import AnalyticsDashboard from './pages/AnalyticsDashboard'
import OTDRPage from './pages/OTDRPage'

function AppContent() {
  const { user, loading, logout, isAuthenticated, isAdmin } = useAuth();
  const { selectedProject, setSelectedProject } = useProject();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  const handleTabChange = (event, newValue) => {
    navigate(newValue);
  };

  const currentTab = location.pathname === '/analytics' ? '/analytics' : 
                     location.pathname === '/otdr' ? '/otdr' : '/';

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            📦 Shipment Tracking Dashboard
          </Typography>
          
          {/* Project Selector in Header */}
          <Box sx={{ mr: 2, minWidth: 200 }}>
            <ProjectSelector 
              onProjectSelect={(projectId) => {
                // This will be handled by the ProjectSelector's handleProjectChange
              }} 
              selectedProjectId={selectedProject?.id}
              showInHeader={true}
            />
          </Box>
            
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.username} ({user?.is_admin ? 'Admin' : 'User'})
            </Typography>
            {isAdmin && (
              <Button 
                color="inherit" 
                startIcon={<AdminIcon />}
                onClick={() => setShowAdminPanel(true)}
                sx={{ mr: 1 }}
              >
                Admin Panel
              </Button>
            )}
            <Button color="inherit" onClick={logout}>
              Logout
            </Button>
          </Toolbar>
          
          {/* Navigation Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.dark' }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)' },
                '& .Mui-selected': { color: 'white' },
                '& .MuiTabs-indicator': { backgroundColor: 'white' }
              }}
            >
              <Tab 
                label="Dashboard" 
                value="/" 
                icon={<DashboardIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Analytics" 
                value="/analytics" 
                icon={<AnalyticsIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="OTDR" 
                value="/otdr" 
                icon={<OTDRIcon />} 
                iconPosition="start"
              />
            </Tabs>
          </Box>
        </AppBar>
        
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
          <Routes>
            <Route path="/" element={<Dashboard projectId={selectedProject?.id} />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/otdr" element={<OTDRPage />} />
          </Routes>
        </Container>

        {/* Admin Panel */}
        <AdminPanel 
          open={showAdminPanel} 
          onClose={() => setShowAdminPanel(false)} 
        />
      </Box>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ProjectProvider>
          <AppContent />
        </ProjectProvider>
      </AuthProvider>
    </Router>
  )
}

export default App